import { useRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Truck, PackageSearch, Warehouse, Plane, Container, MapPinned,
  ArrowRight, ArrowUpRight, Phone, Mail, MapPin, ShieldCheck, Route, Gauge, BadgeCheck,
  Wind, ShieldAlert, Layers, Cog, Mountain, Zap, Lock, CheckCircle2,
} from "lucide-react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { FaqSection } from "./Faq";
import { Reveal } from "./anim";
import { RevealText, HorizontalGallery, StickySteps, ScrollZoom, CustomCursor, Magnetic } from "./scroll";
import { HighwayDivider, GearIn } from "./transport";
import { SmoothScroll, Preloader, CountScene } from "./experience";
import { FestivalHero } from "./FestivalHero";
import { FestivalTeaser } from "./FestivalTeaser";
import { getActiveFestival, getUpcomingFestival, applyRegion, detectRegionZone } from "./festivals";
import { SERVICES, STATS, CONTACT, IMAGES, FLEET, DEFENCE, USPS, SINCE, HOME_FAQ, SITE, faqSchema } from "./marketingData";

const SERVICE_ICONS = { Truck, PackageSearch, Warehouse, Plane, Container, MapPinned };
const FLEET_ICONS = { Wind, ShieldAlert, Layers, Cog, Truck, PackageSearch };

const GALLERY = [
  { img: IMAGES.odcTransformer, tag: "ODC", label: "Transformer movement" },
  { img: IMAGES.industrialTank, tag: "Tanks", label: "Industrial & chemical tanks" },
  { img: IMAGES.tankTrailer, tag: "Defence", label: "Battle tank transport" },
  { img: IMAGES.airCargo, tag: "Air", label: "Domestic air cargo" },
  { img: IMAGES.odcExcavator, tag: "Machinery", label: "Construction machinery" },
  { img: IMAGES.lehConvoy, tag: "Border", label: "Leh & Ladakh convoys" },
  { img: IMAGES.fleetYard, tag: "Fleet", label: "800+ own vehicles" },
];

const STEPS = [
  { title: "Route survey", desc: "We physically survey the road, bridges and clearances before the load ever moves.", image: IMAGES.lehConvoy },
  { title: "Permits & clearances", desc: "State permits, over-dimension approvals and obstruction clearances secured end to end.", image: IMAGES.odcTransformer },
  { title: "Pilot & escort convoy", desc: "Pilot vehicles, police and army escort coordination, with night-movement planning.", image: IMAGES.explosiveVan },
  { title: "Delivered, on time", desc: "Live-tracked to the destination pincode — even the highest border regions.", image: IMAGES.fleetYard },
];

// Motion-enabled button helpers (subtle press + hover).
const btnMotion = { whileHover: { scale: 1.04 }, whileTap: { scale: 0.97 } };

export default function MarketingHome() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const progressX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(heroProgress, [0, 1], ["0%", "22%"]);
  const heroScale = useTransform(heroProgress, [0, 1], [1, 1.12]);
  const heroFade = useTransform(heroProgress, [0, 0.8], [1, 0]);

  // Festival greeting hero (auto on festival days; ?festival=<key> to preview).
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  // Visitor's region → localised festival name (e.g. Pongal in the South, Bihu in the NE).
  // Preview with ?region=south|north|east|west|northeast.
  const [zone, setZone] = useState(() => params.get("region") || null);
  useEffect(() => {
    if (params.get("region")) return; // manual override wins
    let alive = true;
    detectRegionZone().then((z) => { if (alive && z) setZone(z); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const activeFestival = getActiveFestival(params.get("festival") || params.get("fest"));
  const festival = applyRegion(activeFestival, zone);
  // Countdown teaser in the days leading up to a festival (hidden when a festival hero is active).
  const teaserKey = params.get("teaser");
  const upcoming = festival
    ? null
    : getUpcomingFestival(7, teaserKey ? { key: teaserKey, days: Number(params.get("days")) || 3 } : undefined);

  return (
    <div className="bg-[#F9F8F6] text-[#1A1A18] font-body antialiased overflow-x-hidden md:[cursor:none]">
      <Preloader />
      <SmoothScroll />
      <CustomCursor />
      <Seo
        title="ITO — ODC, Defence & Machinery Transport in India Since 1972"
        description="India's trusted transporter since 1972 — air-suspension trailers, explosive vans, ODC & over-dimensional cargo, defence equipment, tanks, drones, oil & gas modules and construction machinery. 800+ own fleet. Fastest, safest, most reliable to every pincode, including Leh."
        path="/"
        image={IMAGES.odcTransformer}
        imageAlt="Heavy transport on an Indian highway — Indian Transport Organisation"
        keywords="transporter in india, best transporter in india, cargo transport india, logistics company india, ODC transport india, over dimensional cargo india, heavy haulage india, heavy haul transport, trailer transport india, air suspension trailer india, low bed trailer india, modular heavy haul trailer india, defence logistics india, tank transport india, drone transport india, chemical tank transport india, machinery transport india, project cargo india, air cargo india, full truck load india, part load transport india, warehousing india, reefer truck india, temperature controlled chemical transport india, aero engine transport india, aerospace logistics india, pan india transporter, Leh Ladakh cargo transport, Mumbai transporter, Indian Transport Organisation, ITO logistics, transport company since 1972"
        jsonLd={[
          { "@context": "https://schema.org", "@type": "WebSite", name: "Indian Transport Organisation", url: `${SITE}/`, publisher: { "@type": "Organization", name: "Indian Transport Organisation", url: `${SITE}/` } },
          faqSchema(HOME_FAQ),
        ]}
      />
      {/* Scroll progress bar */}
      <motion.div style={{ scaleX: progressX }} className="fixed top-0 left-0 right-0 h-1 bg-[#C84B31] origin-left z-[60]" />
      <MarketingNav heroDark />

      {/* ---------------- HERO ---------------- */}
      {festival ? (
        <FestivalHero festival={festival} />
      ) : (
        <section ref={heroRef} className="relative min-h-[100vh] flex items-end pb-16 pt-28 overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 will-change-transform">
          <img src={IMAGES.heroHighway} alt="Heavy transport on an Indian highway" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A18]/90 via-[#1A1A18]/60 to-[#1A1A18]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/75 to-transparent" />
        </motion.div>

        <motion.div style={{ opacity: heroFade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-xs font-semibold tracking-[0.25em] uppercase text-[#D4A373] mb-5"
          >
            Since {SINCE} · Machinery · Defence · Tanks · ODC · Oil &amp; Gas · Air Cargo
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-7xl max-w-4xl leading-[1.02]"
          >
            We move India's heaviest, most critical cargo.
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-7 space-y-2.5"
          >
            {["Covering every pincode of India", "Transporting via land and air", "Parcel to ODC"].map((line) => (
              <div key={line} className="flex items-center gap-3 text-white">
                <span className="h-6 w-1 rounded-full bg-[#C84B31]" />
                <span className="font-heading font-semibold text-lg sm:text-xl md:text-2xl tracking-tight">{line}</span>
              </div>
            ))}
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-5 text-base text-white/85 max-w-xl leading-relaxed"
          >
            One accountable partner for your heaviest and most critical cargo — trusted across the country since {SINCE}.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Magnetic>
              <motion.a {...btnMotion} href={CONTACT.phoneHref} data-testid="hero-quote-btn" data-cursor
                className="group inline-flex items-center gap-2 rounded-full bg-[#C84B31] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
                <Phone className="w-4 h-4" /> Get a quote
              </motion.a>
            </Magnetic>
            <Magnetic>
              <motion.a {...btnMotion} href="#services" data-testid="hero-services-btn" data-cursor
                className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white hover:text-[#1A1A18] transition-colors">
                Explore services <ArrowRight className="w-4 h-4" />
              </motion.a>
            </Magnetic>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3"
          >
            {USPS.map((u, i) => (
              <motion.span key={u}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.6 + i * 0.1 }}
                className="inline-flex items-center gap-2 text-sm font-semibold text-white/90">
                <CheckCircle2 className="w-4 h-4 text-[#D4A373]" /> {u}
              </motion.span>
            ))}
          </motion.div>
        </motion.div>
      </section>
      )}

      {/* ---------------- STATS (pinned count-up scene) ---------------- */}
      <CountScene
        stats={STATS}
        eyebrow="Trusted at scale · since 1972"
        heading="Half a century of moving India — by the numbers."
      />

      {/* ---------------- LOGISTICS MARQUEE ---------------- */}
      <section className="bg-[#C84B31] text-white py-4 overflow-hidden select-none" data-testid="logistics-marquee" aria-hidden="true">
        <div className="marquee-mask">
          <div className="flex w-max animate-marquee whitespace-nowrap">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex items-center">
                {["Pan-India delivery", "Road & Air", "ODC & heavy haul", "Defence logistics", "Air-suspension trailers", "Explosive vans", "Every pincode", "Since 1972"].map((t, i) => (
                  <span key={t + dup} className="flex items-center text-sm font-heading font-bold uppercase tracking-widest">
                    <span className="px-6">{t}</span>
                    {i % 2 === 0 ? <Truck className="w-4 h-4 opacity-80" /> : <Plane className="w-4 h-4 opacity-80" />}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- SERVICES ---------------- */}
      <section id="services" className="py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#C84B31] mb-3">What we haul</p>
            <RevealText as="h2" className="font-heading font-bold tracking-tight text-3xl md:text-4xl"
              text="Six ways we get it there — from a single crate to a battle tank." />
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[s.icon] || Truck;
              return (
                <GearIn key={s.slug} delay={(i % 3) * 0.08}>
                  <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} data-cursor
                    className="group block h-full rounded-2xl bg-white border border-[#1A1A18]/10 overflow-hidden hover:border-[#C84B31]/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="relative h-48 overflow-hidden">
                      <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/60 to-transparent" />
                      <span className="absolute top-4 left-4 rounded-full bg-[#C84B31] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">{s.tag}</span>
                    </div>
                    <div className="p-7">
                      <div className="flex items-center gap-3 mb-3">
                        <Icon className="w-5 h-5 text-[#C84B31]" />
                        <h3 className="font-heading font-semibold text-xl">{s.title}</h3>
                      </div>
                      <p className="text-sm text-[#4F4C44] leading-relaxed">{s.short}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">
                        Learn more
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                  </Link>
                </GearIn>
              );
            })}
          </div>
        </div>
      </section>

      <HighwayDivider />

      {/* ---------------- AUTO-MARQUEE IMAGE GALLERY ---------------- */}
      <section className="py-16 bg-[#1A1A18] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">In motion, across India</p>
          <RevealText as="h2" className="font-heading font-bold tracking-tight text-3xl md:text-5xl leading-tight max-w-3xl"
            text="Every load, engineered — see the work in motion." />
        </div>
      </section>
      <div className="bg-[#1A1A18] pb-16">
        <HorizontalGallery>
          {GALLERY.map((g) => (
            <div key={g.label} data-cursor
              className="relative shrink-0 w-[300px] sm:w-[420px] h-[380px] sm:h-[500px] rounded-3xl overflow-hidden group">
              <img src={g.img} alt={g.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/80 via-transparent to-transparent" />
              <span className="absolute top-5 left-5 rounded-full bg-[#C84B31] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white">{g.tag}</span>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-heading font-bold text-white text-2xl">{g.label}</div>
              </div>
            </div>
          ))}
        </HorizontalGallery>
      </div>

      {/* ---------------- BIG ODC / HEAVY ENGINEERING SHOWCASE ---------------- */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-3xl mb-12">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#C84B31] mb-3">Heavy engineering in motion</p>
            <RevealText as="h2" className="font-heading font-bold tracking-tight text-3xl md:text-5xl leading-tight"
              text="Over-dimensional cargo is our home ground." />
            <p className="mt-5 text-base text-[#4F4C44] leading-relaxed">
              Transformers, turbines, excavators, oil &amp; gas modules, industrial &amp; chemical
              storage tanks and battle tanks — moved on specialised low-bed and modular trailers,
              route-surveyed and escorted across India.
            </p>
          </Reveal>
        </div>

        {/* Full-bleed large image with scroll-linked zoom */}
        <Reveal className="relative">
          <ScrollZoom className="relative h-[62vh] min-h-[440px] w-full">
            <img src={IMAGES.odcTransformer} alt="Giant transformer on a heavy-haul trailer on an Indian highway" className="w-full h-full object-cover" />
          </ScrollZoom>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1A1A18]/70 via-transparent to-transparent" />
          <div className="absolute bottom-8 left-0 right-0">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
              <span className="inline-block rounded-full bg-[#C84B31] px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white">
                Heavy-haul trailer · Transformer movement
              </span>
            </div>
          </div>
        </Reveal>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-8 grid md:grid-cols-2 gap-8">
          <Reveal className="relative rounded-2xl overflow-hidden h-[420px] group">
            <ScrollZoom className="w-full h-full" from={1.18}>
              <img src={IMAGES.odcExcavator} alt="Excavator on a low-bed trailer on an Indian highway" className="w-full h-full object-cover" />
            </ScrollZoom>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1A1A18]/70 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <div className="font-heading font-bold text-white text-2xl">Construction machinery</div>
              <div className="text-white/85 text-sm">Excavators &amp; plant on low-bed trailers</div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="relative rounded-2xl overflow-hidden h-[420px] group">
            <ScrollZoom className="w-full h-full" from={1.18}>
              <img src={IMAGES.airSuspension} alt="Air-suspension trailer carrying machinery on an Indian expressway" className="w-full h-full object-cover" />
            </ScrollZoom>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1A1A18]/70 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <div className="font-heading font-bold text-white text-2xl">Air-suspension transit</div>
              <div className="text-white/85 text-sm">Shock-free rides for sensitive machinery</div>
            </div>
          </Reveal>
        </div>
      </section>

      <HighwayDivider />

      {/* ---------------- HOW WE MOVE IT ---------------- */}
      <StickySteps steps={STEPS} eyebrow="How we move it" heading="Four steps from plant gate to destination." />


      {/* ---------------- OWN FLEET ---------------- */}
      <section id="fleet" className="py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
            <Reveal className="lg:col-span-7">
              <p className="text-xs font-semibold tracking-widest uppercase text-[#C84B31] mb-3">Our own fleet</p>
              <h2 className="font-heading font-bold tracking-tight text-3xl md:text-4xl leading-tight">
                800+ owned vehicles — from tempos to heavy-haul trailers.
              </h2>
              <p className="mt-5 text-base text-[#4F4C44] leading-relaxed">
                We don't just broker trucks — we run our own fleet of 800+ vehicles: tempos, heavy-haul
                trucks, high-bed, low-bed and semi low-bed trailers, air-suspension trailers and
                licensed explosive vans. Owning the fleet means tighter control over safety, timing and
                the specialised equipment your cargo demands.
              </p>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5 rounded-2xl overflow-hidden h-56">
              <img src={IMAGES.fleetYard} alt="ITO fleet depot yard in India" className="w-full h-full object-cover" />
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FLEET.map((f, i) => {
              const Icon = FLEET_ICONS[f.icon] || Truck;
              const featured = f.name === "Air-suspension trailers" || f.name === "Explosive vans";
              return (
                <GearIn key={f.name} delay={(i % 3) * 0.07}>
                  <Link to={`/fleet/${f.slug}`} data-testid={`fleet-card-${i}`} data-cursor
                    className={`group block h-full rounded-2xl p-7 border transition-all duration-300 hover:-translate-y-1 ${
                      featured
                        ? "bg-[#1A1A18] border-[#1A1A18] text-white hover:shadow-xl"
                        : "bg-white border-[#1A1A18]/10 hover:border-[#C84B31]/50 hover:shadow-lg"
                    }`}>
                    <span className={`inline-flex w-11 h-11 rounded-xl items-center justify-center mb-5 ${featured ? "bg-[#C84B31]" : "bg-[#C84B31]/10"}`}>
                      <Icon className={`w-5 h-5 ${featured ? "text-white" : "text-[#C84B31]"}`} />
                    </span>
                    <h3 className={`font-heading font-semibold text-lg mb-2 ${featured ? "text-white" : "text-[#1A1A18]"}`}>{f.name}</h3>
                    <p className={`text-sm leading-relaxed ${featured ? "text-white/85" : "text-[#4F4C44]"}`}>{f.desc}</p>
                    <span className={`mt-4 inline-flex items-center gap-1.5 text-sm font-semibold ${featured ? "text-[#D4A373]" : "text-[#C84B31]"}`}>
                      View details <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                </GearIn>
              );
            })}
          </div>
        </div>
      </section>

      <HighwayDivider />

      {/* ---------------- DEFENCE ---------------- */}
      <section id="defence" className="py-24 bg-[#1A1A18] text-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">Indian defence logistics</p>
              <RevealText as="h2" className="font-heading font-bold tracking-tight text-3xl md:text-5xl leading-tight" text={DEFENCE.title} />
              <p className="mt-5 text-white/75 leading-relaxed">{DEFENCE.body}</p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-4">
                {DEFENCE.points.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm">
                    <ShieldCheck className="w-5 h-5 text-[#C84B31] shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <Link to="/services/defence-logistics" data-testid="defence-cta"
                className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#C84B31] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
                Move defence cargo <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden h-56 col-span-2">
                  <img src={IMAGES.tankTrailer} alt="Indian Army tank on a heavy low-bed trailer" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden h-44">
                  <img src={IMAGES.defenceDrone} alt="Military drone UAV prepared for road transport in India" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden h-44">
                  <img src={IMAGES.explosiveVan} alt="Explosive van with army escort on an Indian highway" className="w-full h-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- HILLY / BORDER / LEH EXPERTISE ---------------- */}
      <section className="relative py-28 overflow-hidden">
        <ScrollZoom className="absolute inset-0" from={1.3}>
          <img src={IMAGES.lehConvoy} alt="Convoy on a high-altitude Himalayan road near Leh Ladakh" className="w-full h-full object-cover" />
        </ScrollZoom>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A18]/85 to-[#1A1A18]/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">
              <Mountain className="w-4 h-4" /> Hilly &amp; border regions
            </p>
            <RevealText as="h2" className="font-heading font-bold tracking-tight text-3xl md:text-5xl text-white leading-tight"
              text="India's most experienced in the hardest terrain — Leh, Ladakh & the Himalayas." />
            <p className="mt-5 text-base text-white/80 leading-relaxed">
              High-altitude passes, sub-zero temperatures, hairpin bends and remote forward areas — we've
              been delivering here for decades. When defence and infrastructure cargo has to reach the
              border regions, ITO is the name that gets it there, safely and on time.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
              {[
                { Icon: Mountain, t: "High-altitude proven" },
                { Icon: Lock, t: "Escorted & secure" },
                { Icon: Zap, t: "Fastest to the front" },
              ].map((x) => (
                <span key={x.t} className="inline-flex items-center gap-2 text-sm font-semibold text-white">
                  <x.Icon className="w-4 h-4 text-[#C84B31]" /> {x.t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- AIR CARGO BAND ---------------- */}
      <section className="relative py-28 overflow-hidden">
        <ScrollZoom className="absolute inset-0" from={1.28}>
          <img src={IMAGES.airCargo} alt="Indian air cargo being loaded into a freighter" className="w-full h-full object-cover" />
        </ScrollZoom>
        <div className="absolute inset-0 bg-[#1A1A18]/72" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">Time-critical? Fly it.</p>
            <RevealText as="h2" className="font-heading font-bold tracking-tight text-3xl md:text-4xl text-white leading-tight"
              text="Domestic Indian air cargo for grounded machines and priority defence stores." />
            <p className="mt-5 text-base text-white/80 leading-relaxed">
              When a production line is down or a unit is grounded, hours matter. We book and uplift your
              cargo on domestic Indian freighters and belly-hold capacity — airport-to-door, on a single
              tracked Lorry Receipt.
            </p>
            <Link to="/services/air-express-cargo" data-testid="air-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C84B31] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
              <Plane className="w-4 h-4" /> Air &amp; express cargo
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------------- WHY ITO ---------------- */}
      <section id="why" className="py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#C84B31] mb-3">Why ITO · since {SINCE}</p>
            <RevealText as="h2" className="font-heading font-bold tracking-tight text-3xl md:text-4xl"
              text="The fastest, safest and most reliable name in Indian heavy transport." />
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { h: "Trusted across the country", p: "Half a century of moving India's most critical cargo — with 23+ offices and 2,500+ staff on the ground." },
              { h: "Built for heavy & sensitive", p: "800+ owned vehicles — air-suspension trailers, explosive vans and specialised trailers matched to your exact cargo." },
              { h: "Fastest, safest, most reliable", p: "Route-surveyed, escorted and live-tracked to every pincode — even the highest border regions." },
            ].map((c, i) => (
              <GearIn key={c.h} delay={i * 0.08}
                className="rounded-2xl bg-white border border-[#1A1A18]/10 p-8 hover:border-[#C84B31]/40 hover:shadow-lg transition-all duration-300">
                <div className="font-heading font-black text-5xl text-[#C84B31]/15 leading-none mb-4">0{i + 1}</div>
                <h3 className="font-heading font-semibold text-lg mb-2">{c.h}</h3>
                <p className="text-sm text-[#4F4C44] leading-relaxed">{c.p}</p>
              </GearIn>
            ))}
          </div>
        </div>
      </section>

      <HighwayDivider />

      {/* ---------------- CONTACT ---------------- */}
      <section id="contact" className="py-24 bg-[#1A1A18] text-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">Head office · Mumbai</p>
            <RevealText as="h2" className="font-heading font-bold tracking-tight text-3xl md:text-5xl leading-tight" text="Tell us what needs moving." />
            <p className="mt-5 text-white/85 max-w-md leading-relaxed">
              From a single machine spare to a fleet of tanks — call or email us and our team will quote
              your movement, by road or air, to any pincode in India.
            </p>
            <div className="mt-10 space-y-6">
              <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-full bg-[#C84B31] flex items-center justify-center shrink-0"><Phone className="w-5 h-5" /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-white/75">Call us</span>
                  <span className="text-xl font-heading font-bold group-hover:text-[#D4A373] transition-colors">{CONTACT.phone}</span>
                </span>
              </a>
              <a href={CONTACT.emailHref} data-testid="contact-email" className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-full bg-[#C84B31] flex items-center justify-center shrink-0"><Mail className="w-5 h-5" /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-white/75">Email us</span>
                  <span className="text-lg font-heading font-bold group-hover:text-[#D4A373] transition-colors break-all">{CONTACT.email}</span>
                </span>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#D4A373]" /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-white/75">Head office</span>
                  <span className="text-base text-white/90">{CONTACT.address}</span>
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="rounded-2xl overflow-hidden border border-white/10 h-[420px]">
            <iframe title="ITO head office location" data-testid="contact-map"
              className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              src={
                process.env.REACT_APP_GOOGLE_MAPS_API_KEY
                  ? `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(CONTACT.mapQuery)}&zoom=17`
                  : `https://maps.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&t=&z=17&ie=UTF8&iwloc=&output=embed`
              } />
          </Reveal>
        </div>
      </section>

      {upcoming && <FestivalTeaser festival={upcoming.festival} days={upcoming.days} />}

      <FaqSection faqs={HOME_FAQ} title="Frequently asked questions" testid="home-faq" />

      <MarketingFooter />
    </div>
  );
}
